import { CheckCircle, Users, Award, Target, TrendingUp, Shield, Sparkles, ArrowRight, Trophy, BadgeCheck, Cpu, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const About = () => {
  const stats = [
    { number: "1000+", label: "Brands Onboarded", icon: "🏆" },
    { number: "70+", label: "Partner Platforms", icon: "🛒" },
    { number: "98%", label: "Success Rate", icon: "✅" },
    { number: "10+", label: "Years Team Experience", icon: "📅" }
  ];

  const values = [
    {
      icon: Target,
      title: "Brand-First Strategy",
      description: "We tailor every marketplace solution around your brand's unique identity and goals."
    },
    {
      icon: Award,
      title: "Marketplace Expertise",
      description: "We specialize in onboarding and scaling brands on premium platforms like Myntra, Nykaa, and Ajio."
    },
    {
      icon: TrendingUp,
      title: "Data-Driven Decisions",
      description: "Our actions are backed by insights to maximize visibility, conversion, and growth."
    },
    {
      icon: Shield,
      title: "Full-Spectrum Support",
      description: "From listing to logistics, we manage the complete journey for effortless retail success."
    }
  ];

  const achievements = [
    {
      icon: Trophy,
      color: "from-amber-400 to-orange-500",
      title: "#1 Brand Onboarding Company in India",
      desc: "Ranked India's #1 e-commerce brand onboarding agency with 1000+ brands launched and a 98% first-time approval rate."
    },
    {
      icon: BadgeCheck,
      color: "from-pink-500 to-rose-500",
      title: "Official OMS Partner of Myntra",
      desc: "BrandBorn is a registered Official OMS (Onboarding & Marketplace Services) Partner of Myntra — direct access, faster approvals."
    },
    {
      icon: Cpu,
      color: "from-blue-500 to-indigo-500",
      title: "India's 1st AI Listings Software — EcomCatalog.ai",
      desc: "We built EcomCatalog.ai, India's first AI auto listings & data analysis software for e-commerce sellers. Marketplace-ready catalogs in 90 seconds."
    },
    {
      icon: Newspaper,
      color: "from-emerald-500 to-green-500",
      title: "Featured in Times of India & Economic Times",
      desc: "Our e-commerce onboarding expertise has been featured in The Times of India and The Economic Times."
    }
  ];

  const team = [
    {
      name: "Expert Team",
      role: "Marketplace Specialists",
      description: "Dedicated marketplace specialists with years of experience in e-commerce onboarding."
    },
    {
      name: "Growth Focus",
      role: "Data Analytics",
      description: "Data-driven strategies for maximum visibility and sustainable growth."
    },
    {
      name: "24/7 Support",
      role: "Customer Success",
      description: "End-to-end management from listing to logistics with round-the-clock assistance."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About BrandBorn - India's #1 Marketplace Onboarding Agency | 1000+ Brands Launched</title>
        <meta name="description" content="Learn about BrandBorn - India's leading marketplace onboarding agency with 1000+ brands launched, 98% approval rate. Expert team for Myntra, Nykaa, Ajio, Blinkit onboarding. Call +91-9464645491" />
        <link rel="canonical" href="https://www.brandborn.in/about" />
        <meta property="og:title" content="About BrandBorn - India's #1 Marketplace Onboarding Agency" />
        <meta property="og:description" content="1000+ brands launched with 98% approval rate. Expert marketplace onboarding for Myntra, Nykaa, Ajio, Blinkit." />
        <meta property="og:url" content="https://www.brandborn.in/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-red-50 via-white to-orange-50 relative overflow-hidden">
        <div className="absolute top-20 right-[10%] w-[400px] h-[400px] bg-gradient-to-br from-red-200/30 to-orange-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-[5%] w-[300px] h-[300px] bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full text-sm font-bold mb-6 shadow-lg">
              <Sparkles size={16} />
              ABOUT US
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6">
              About <span className="text-gradient bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">BrandBorn</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              India's #1 E-commerce Onboarding Agency helping brands succeed on 70+ premium online marketplaces since 2024.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, idx) => (
              <div key={idx} className="stat-card depth-8d text-center">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="stat-number text-3xl">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 px-4 bg-white" data-testid="about-page-achievements">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold mb-4">
              OUR ACHIEVEMENTS
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Why BrandBorn is India's <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">#1 Brand Onboarding Company</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Official Myntra OMS Partner • India's 1st AI Listings Software (EcomCatalog.ai) • Featured in Times of India & Economic Times • Certified by BEEP, Google & Great Learning
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {achievements.map((item, idx) => (
              <div
                key={idx}
                data-testid={`about-page-achievement-${idx}`}
                className="group bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-base font-black text-gray-900 mb-2 leading-snug">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gray-50 rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid="about-page-certifications">
            <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <Award className="w-5 h-5 text-red-500" /> Certified By:
              </span>
              {["BEEP Certified", "Google Certified", "Great Learning Certified"].map((c, i) => (
                <span key={i} className={`px-4 py-2 rounded-full text-xs font-bold shadow-sm border ${
                  i === 0 ? 'bg-blue-50 text-blue-700 border-blue-100' :
                  i === 1 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                  'bg-orange-50 text-orange-700 border-orange-100'
                }`}>
                  <CheckCircle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{c}
                </span>
              ))}
              <span className="hidden md:inline-block w-px h-6 bg-gray-200" />
              <span className="flex items-center gap-3 text-xs font-bold text-gray-500">
                <Newspaper className="w-4 h-4 text-gray-400" /> Featured In:
                <img src="/images/logos/times-of-india.png" alt="The Times of India" className="h-8 object-contain" loading="lazy" />
                <img src="/images/logos/economic-times.png" alt="The Economic Times" className="h-5 object-contain" loading="lazy" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold mb-4">
                OUR MISSION
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">
                BrandBorn Mission & Goals
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                BrandBorn's mission is to empower fashion, beauty, and lifestyle brands by simplifying their entry into premium online marketplaces and enabling sustainable growth.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We believe every brand deserves access to India's booming e-commerce market. Our expert team handles the complex onboarding process, allowing you to focus on what you do best - building your brand.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full">
                  <CheckCircle size={18} />
                  <span className="font-semibold">100% Results Guaranteed</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full">
                  <CheckCircle size={18} />
                  <span className="font-semibold">1000+ Projects Done</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-3xl p-1">
                <div className="bg-white rounded-[22px] p-8">
                  <img 
                    src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop"
                    alt="E-commerce team at work"
                    className="rounded-2xl w-full h-64 object-cover"
                  />
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-2xl font-bold text-red-500">4K+</div>
                      <div className="text-sm text-gray-600">Projects Done</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-2xl font-bold text-red-500">100%</div>
                      <div className="text-sm text-gray-600">Results Guaranteed</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold mb-4">
              WHY CHOOSE US
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Why Choose BrandBorn?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              India's #1 Marketplace Onboarding Service with 1000+ brands onboarded
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div key={idx} className="service-card card-8d">
                  <div className="service-icon bg-red-100">
                    <IconComponent className="text-red-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold mb-4">
              OUR EXPERTISE
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Expert Team at Your Service
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, idx) => (
              <div key={idx} className="text-center p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <Users className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-red-500 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold mb-4">
              LEADERSHIP
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Meet Our Founder
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 shadow-lg">
              <div className="flex-shrink-0">
                <img
                  src="/images/team/ceo.jpeg"
                  alt="Anmol Singh - CEO & Founder of BrandBorn"
                  className="w-48 h-48 md:w-56 md:h-56 rounded-2xl object-cover shadow-xl"
                />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Anmol Singh</h3>
                <p className="text-red-500 font-semibold text-lg mb-4">Founder & CEO, BrandBorn</p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  With over <strong className="text-gray-900">10 years of experience</strong> in e-commerce and marketplace management, Anmol Singh founded BrandBorn with a vision to help Indian brands succeed on online marketplaces. Under his leadership, BrandBorn has grown to become India's #1 marketplace onboarding agency with 1000+ brands launched and a 98% approval rate.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  His deep expertise in fashion, beauty, and FMCG marketplace ecosystems — combined with strong relationships across Myntra, Nykaa, Ajio, Blinkit, and other platforms — ensures every brand gets the best possible launch strategy.
                </p>
                <a
                  href="https://www.linkedin.com/in/anmol-singh-47431112a"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] text-white rounded-full font-semibold hover:bg-[#004182] transition-all"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
            Ready to Launch Your Brand?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join 1000+ successful brands that trust BrandBorn for their marketplace journey.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white text-red-500 px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
          >
            Get Started Today
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <style jsx>{`
        .text-gradient {
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>
    </div>
  );
};

export default About;
